/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { PriceDisplay } from '@/components/blocks/PriceDisplay';
import { titleSideBarSX } from '@/components/blocks/SideBar/styles/typography';
import { CatalogEntryList } from '@/components/content/CatalogEntryList';
import { CardEntry } from '@/components/content/FacetNavigation/parts/CardEntry';
import { FacetNavigationEntry } from '@/components/content/FacetNavigation/parts/Entry';
import { facetNavigationAccordionSX } from '@/components/content/FacetNavigation/styles/accordion';
import { facetNavigationAccordionSummarySX } from '@/components/content/FacetNavigation/styles/accordionSummary';
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { useFacetNavigation } from '@/data/Content/FacetNavigation';
import { useLocalization } from '@/data/Localization';
import { FACET_PRICE_PREFIX } from '@/data/constants/facet';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { ContentContext } from '@/data/context/content';
import { ID } from '@/data/types/Basic';
import { excludeRibbons } from '@/utils/mergeRibbonsForProduct';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Breakpoint,
	Button,
	Chip,
	Grid,
	Stack,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FC, Fragment, SyntheticEvent, useCallback, useContext, useState } from 'react';

const mobileBreakpoint: Breakpoint = 'md';

const facetNamesExcludedElectronico = ['Tallas', 'Genero'];
const facetNamesExcluded = ['Categoría', 'DPT_Oferta', 'DPT_SAPOferta'];
const facetPrefixesExcluded = excludeRibbons;

export const FacetNavigationItemChange: FC<{ id?: ID }> = ({ id }) => {
	const productFilterNLS = useLocalization('ProductFilter');
	const { breadcrumb } = useBreadcrumbTrail();
	const { onPriceRangeChange, filteredParams, toggleFacetLimit, facets } = useContext(
		ContentContext
	) as ReturnType<typeof useFacetNavigation>;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
	const [activeFacetId, setActiveFacetId] = useState<string[]>(() =>
		facets.slice(0, 6).map((facet) => facet?.value ?? '')
	);
	const handleAccordionChange = useCallback(
		(facetValue: string) => (_event: SyntheticEvent<Element, Event>, isExpanded: boolean) => {
			isMobile
				? setActiveFacetId([isExpanded ? facetValue : ''])
				: setActiveFacetId(
						isExpanded
							? [...activeFacetId, facetValue]
							: activeFacetId.filter((e) => facetValue !== e)
				  );
		},
		[activeFacetId, isMobile]
	);

	const { minPrice = -1, maxPrice = -1 } = filteredParams;
	const onDelete = useCallback(
		() => onPriceRangeChange({ minPrice: null, maxPrice: null }),
		[onPriceRangeChange]
	);

	const isElectronico = breadcrumb?.find((item) =>
		['Electronicos', 'Electrónicos'].includes(item.label)
	);

	const facetsFiltered = facets.filter((facetItem) => {
		if (isElectronico) {
			return !facetNamesExcludedElectronico.includes(facetItem?.name ?? EMPTY_STRING);
		}

		if (facetItem?.name?.includes('Marca') && facetItem?.value?.includes('manufacturer.raw')) {
			return false;
		}

		const isPrefixesExcluded = facetPrefixesExcluded.some(
			(prefix) =>
				facetItem?.extendedData?.propertyvalue?.startsWith(prefix) &&
				facetItem?.extendedData?.propertyvalue !== 'DPT_SAPNuevo'
		);

		return !facetNamesExcluded.includes(facetItem?.name ?? EMPTY_STRING) && !isPrefixesExcluded;
	});

	return (
		<>
			<CatalogEntryList id={id!} isFilterChips />
			{facetsFiltered.map((facetItem) => (
				<Accordion
					key={facetItem?.value}
					id={`productFilter-facet-${facetItem?.name}`}
					data-testid={`productFilter-facet-${facetItem?.name}`}
					expanded={activeFacetId.includes(facetItem?.value ?? '')}
					onChange={handleAccordionChange(facetItem!.value)}
					square={true}
					sx={facetNavigationAccordionSX}
				>
					<AccordionSummary
						aria-controls={`${facetItem?.value}-content`}
						id={`${facetItem?.value}-header`}
						data-testid={`${facetItem?.value}-header`}
						sx={facetNavigationAccordionSummarySX}
						expandIcon={<ExpandMoreIcon />}
					>
						<Typography sx={titleSideBarSX}>
							{(facetItem?.value.startsWith(FACET_PRICE_PREFIX)
								? productFilterNLS.Labels.price.t()
								: facetItem?.extendedData?.fname ?? facetItem?.name
							)?.replace('DPT_', '')}
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ p: 0, mb: '20px' }}>
						{facetItem?.value.startsWith(FACET_PRICE_PREFIX) ? (
							minPrice > -1 && maxPrice > -1 ? (
								<Chip
									size="medium"
									label={<PriceDisplay min={minPrice === 0 ? 1 : minPrice} max={maxPrice} />}
									onClick={onDelete}
									onDelete={onDelete}
									data-testid="product-filter-formatted-price-chip"
									id="product-filter-formatted-price-chip"
									sx={{ backgroundColor: '#2682E7', color: '#fff' }}
								/>
							) : (
								<Stack justifyContent="flex-start" alignItems="flex-start" sx={{ pl: 1 }}>
									<Grid container>
										{facetItem?.entry?.map((entry: any) => (
											<Grid item key={entry.value} xs={12}>
												<FacetNavigationEntry entry={entry} isRangePrice />
											</Grid>
										))}
									</Grid>
									{facetItem?.entry && (facetItem.showMore || facetItem.showLess) ? (
										<Button
											variant="inline"
											data-testid={`productFilter_${facetItem.name}_show_more`}
											id={`productFilter_${facetItem.name}_show_more`}
											onClick={toggleFacetLimit(facetItem.value)}
											sx={{ color: '#2C2C2C' }}
										>
											{facetItem.showMore
												? productFilterNLS.Actions.showMore.t()
												: productFilterNLS.Actions.showLess.t()}
										</Button>
									) : null}
								</Stack>
							)
						) : (facetItem?.extendedData?.fname ?? facetItem?.name) === 'Tallas' ? (
							<Fragment>
								<Grid container display="flex" spacing={1}>
									{facetItem?.entry?.map((entry: any) => (
										<Grid item key={entry.value} xs={4}>
											<CardEntry entry={entry} />
										</Grid>
									))}
								</Grid>
								{facetItem?.entry && (facetItem.showMore || facetItem.showLess) ? (
									<Button
										variant="inline"
										data-testid={`productFilter_${facetItem.name}_show_more`}
										id={`productFilter_${facetItem.name}_show_more`}
										onClick={toggleFacetLimit(facetItem.value)}
										sx={{ color: '#2C2C2C' }}
									>
										{facetItem.showMore
											? productFilterNLS.Actions.showMore.t()
											: productFilterNLS.Actions.showLess.t()}
									</Button>
								) : null}
							</Fragment>
						) : (
							<Stack justifyContent="flex-start" alignItems="flex-start" sx={{ pl: 1 }}>
								<Grid container>
									{facetItem?.entry?.map((entry: any) => (
										<Grid item key={entry.value} xs={12}>
											<FacetNavigationEntry entry={entry} />
										</Grid>
									))}
								</Grid>
								{facetItem?.entry && (facetItem.showMore || facetItem.showLess) ? (
									<Button
										variant="inline"
										data-testid={`productFilter_${facetItem.name}_show_more`}
										id={`productFilter_${facetItem.name}_show_more`}
										onClick={toggleFacetLimit(facetItem.value)}
										sx={{ color: '#2C2C2C' }}
									>
										{facetItem.showMore
											? productFilterNLS.Actions.showMore.t()
											: productFilterNLS.Actions.showLess.t()}
									</Button>
								) : null}
							</Stack>
						)}
					</AccordionDetails>
				</Accordion>
			))}
		</>
	);
};
